import jwtDecode from "jwt-decode";

// AUTH-04. What "signed in" means, in one place.
//
// WHAT WAS WRONG
// Nothing ever looked at the token's expiry. `jwt-decode` has been a dependency since
// forever (package.json) and was imported nowhere. A jToken that expired weeks ago still
// counted as a session: the app rendered, every request came back 401, and because there
// was no 401 handler either (the four axios interceptors in the redux services are
// commented-out no-ops), the user saw empty lists and blank pages rather than being asked
// to sign in again. "The app looks broken" and "your session ended" are indistinguishable.

export const TOKEN_KEY = "jToken";
export const AGENT_KEY = "agent";

// localStorage writes the STRING "null"/"undefined" when a null token is stored.
const realValue = (v) => (v && v !== "null" && v !== "undefined" ? v : null);

export const readToken = () => {
  try {
    return realValue(localStorage.getItem(TOKEN_KEY));
  } catch {
    return null;
  }
};

export const readAgent = () => {
  try {
    const raw = localStorage.getItem(AGENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) && Object.keys(parsed).length
      ? parsed
      : null;
  } catch {
    return null;
  }
};

// A token with no exp claim is NOT treated as expired: VT-Backend signs agent tokens
// without one (controllers/agent.js), so assuming an expiry here would sign everybody out.
// Only a claim that has actually passed counts.
export const isTokenExpired = (token = readToken(), now = Date.now()) => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    if (!exp) return false;
    return exp * 1000 <= now;
  } catch {
    // Unparseable is not a session.
    return true;
  }
};

export const isSignedIn = () => {
  const token = readToken();
  if (!token) return false;
  if (isTokenExpired(token)) return false;
  return Boolean(readAgent());
};

export const roleOf = () => {
  const agent = readAgent();
  return agent && typeof agent.role === "string" ? agent.role.trim().toLowerCase() : "";
};

// NOTE, deliberately: this role comes from localStorage, which the user controls, so it
// decides what to SHOW, never what is allowed. Every route that matters is enforced
// server-side — see VT-BE utils/requestIdentity.js, where the same audit finding
// (AUTH-05/06/07) is closed for real.
export const isAdmin = () => roleOf() === "admin";

// Everything a signed-in session owns. currentCompany/companyConfig are the white-label
// selection and survive a sign-out, exactly as the Sidebar's logout always did.
export const clearSession = () => {
  const keep = {};
  ["currentCompany", "companyConfig"].forEach((k) => {
    try {
      const v = localStorage.getItem(k);
      if (v !== null) keep[k] = v;
    } catch { /* storage unavailable */ }
  });
  try {
    localStorage.clear();
    Object.keys(keep).forEach((k) => localStorage.setItem(k, keep[k]));
  } catch { /* storage unavailable */ }
};

export default { readToken, readAgent, isTokenExpired, isSignedIn, roleOf, isAdmin, clearSession };
